/**
 * The links a view opens (docs/views.md#5-navigation-sections-and-links): `coast.open` takes one that `coast.link` built
 * (shared/views/links.ts) back, in place when it is a section of this view and otherwise as its `coast:` link
 * (src/shared/links.ts) or a path for the Coast app to open.
 */
import { parseLink, writeLink } from "../shared/links";

export interface Section {
  name: string;
  params: Record<string, string>;
}

/** A fragment of this view, `#issue?id=…`, as the section it opens; `""` for its start; null for one that isn't. */
function ownSection(own: string): { section: Section } | null {
  if (!own) return { section: { name: "", params: {} } };
  const local = /^#([a-z][a-z0-9_-]{0,39})(?:\?(.*))?$/.exec(own);
  return local
    ? { section: { name: local[1]!, params: Object.fromEntries(new URLSearchParams(local[2] ?? "")) } }
    : null;
}

/**
 * What opening `link` means here: a section of this view, in place, whether it comes as `#issue?id=…` or as a link to
 * this view in any form `parseLink` reads (src/shared/links.ts); another chat or view of Coast's as its `coast:` link, for
 * the Coast app to open on top of the screen it is on; another path on this server or on coast.ws, such as a view's
 * home, kept relative, for the Coast app; any other http(s) link as it is; or nothing. A home's path, `/v/<view>`, is
 * also taken as `/a/<view>`, which it was until 2026-10-17 and which stored messages and records still hold.
 */
export function resolveLink(
  link: string,
  base: string | null | undefined,
  pageOrigin: string,
): { section: Section } | { open: string } | null {
  if (link.startsWith("#")) return ownSection(link);
  const here = parseLink(base, { origins: [pageOrigin, base] });
  const target = parseLink(link, { origins: [pageOrigin, base] });
  if (target) {
    if (here?.view && target.chat === here.chat && target.view === here.view)
      return { section: { name: target.section ?? "", params: target.params ?? {} } };
    return { open: writeLink(target) };
  }
  // A view's home, `https://coast.ws/v/<view>`, which has no `coast:` link, and what isn't Coast's.
  const home = base && !here ? base : null;
  let path = link;
  if (/^https?:\/\//.test(link)) {
    try {
      const url = new URL(link),
        start = home ? new URL(home) : null;
      if (url.origin === pageOrigin || url.hostname === "coast.ws" || (start && url.origin === start.origin))
        path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  const startPath = (() => {
    try {
      return home ? new URL(home).pathname : null;
    } catch {
      return null;
    }
  })();
  const ownPath = startPath?.startsWith("/v/") && path.startsWith("/a/") ? `/v/${path.slice(3)}` : path;
  if (startPath && (ownPath === startPath || ownPath.startsWith(`${startPath}#`)))
    return ownSection(ownPath.slice(startPath.length));
  return /^(https?:\/\/|\/)/.test(path) ? { open: path } : null;
}

/**
 * Links outside Coast, the http(s) links `resolveLink` hands on as they are, open only after a tap or key press in the
 * view, and one a second at most, so a view can't open a page on its own or a page for every call one tap allows
 * (docs/views.md#5-navigation-sections-and-links). Paths stay Coast's to open, as they always could.
 */
export class OutsideLinks {
  static readonly interval = 1000;
  private last = -Infinity;

  /** Whether `link` may open now, given whether the view was just tapped; an outside link that may opens now. */
  allow(link: string, tapped: boolean, now = Date.now()): boolean {
    if (!/^https?:\/\//.test(link)) return true;
    if (!tapped || now - this.last < OutsideLinks.interval) return false;
    this.last = now;
    return true;
  }
}
